import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import {
  isEmptyFilter,
  LEAD_SORT_FIELDS,
  type BulkAction,
  type BulkActionBody,
  type BulkActionResponse,
  type BulkTarget,
  type FilterDefinition,
  type LeadListQuery,
  type LeadListResponse,
  type LeadSortField,
} from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ApiRequestError } from "../api/http";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { PageHeader } from "../design/components/PageHeader";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import "../design/components/text-field.css";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { FileImportButton } from "../imports/FileImportButton";
import { leadScopeOf, smartListScopeOf, useAccess } from "../session/access";
import { NewSmartListDialog } from "../smart-lists/NewSmartListDialog";
import { archivedLeadsQuery } from "./archive/archive-queries";
import { ArchivedDialog } from "./archive/ArchivedDialog";
import "./archive/archive.css";
import { BulkBar, type BulkFeature } from "./bulk/BulkBar";
import { BulkDialog } from "./bulk/BulkDialog";
import { queryTargetOf } from "./bulk/bulk-queries";
import { useBulkSelection, type PickedLead } from "./bulk/bulk-selection";
import { bulkOutcomeText } from "./bulk/bulk-text";
import { dataIssuesQuery, duplicatesQuery } from "./cleanup/cleanup-queries";
import { DataCleanupDialog, type CleanupTab } from "./cleanup/DataCleanupDialog";
import { ColumnsDrawer } from "./columns/ColumnsDrawer";
import { useTableLayout } from "./columns/layout-queries";
import { LEADS_TABLE_SCOPE, savedSortOf, sortDescription, type LeadSort } from "./columns/table-layout";
import { FilterBar } from "./filters/FilterBar";
import { leadCatalogQuery } from "./filters/filter-queries";
import { parseFilterParam } from "./filters/filter-url";
import { LOADING_ROWS, SEARCH_MAX_LENGTH, searchParamText, useColumnArrange, useLeadPages, useResultSearch } from "./lead-list-hooks";
import { LEAD_PAGE_SIZE, useLeadView } from "./lead-queries";
import { LeadTable } from "./LeadTable";
import { NewLeadDialog } from "./NewLeadDialog";
import "./lead-list.css";

// La pagina «Lead» (piano M2 Task 17, mock-up `lead.html` e `initLeadPage`): ricerca in tutti i campi (anche con un
// pezzo del telefono), «Di chi» quando il server lo lascia scegliere, il conteggio, «Nuovo Lead» per chi crea Lead
// per tutti, il pulsante «Filtri» con i filtri attivi (piano M3 Task 4), «Colonne e vista» (Task 6) e la tabella con
// le colonne della vista in uso. Ricerca, «Di chi», filtri e ordinamento stanno nell'indirizzo, così un link o
// «Indietro» ritrovano la stessa vista; le colonne no: sono la vista di chi guarda (D5), sul dispositivo finché non si
// salvano. L'ordinamento fa parte della vista salvata (LEAD-17): senza `sort` nell'indirizzo vale quello della vista in uso,
// e uno scelto dall'intestazione resta nell'indirizzo finché non si salva o non si torna alla vista. «Crea Smart List da questi filtri» (piano M3 Task 8), per chi crea liste, è spento senza filtri con il motivo
// scritto. Sotto la tabella, per chi ha `trash_restore`, «Archiviati (N)» (Task 12) apre la finestra degli archiviati,
// anche dall'indirizzo (`archiviati=1`, con la ricerca in `cercaArchiviati`, un parametro suo e mai il `q` dell'elenco:
// decisione P16). «Azioni multiple» (Task 14), per chi ha `bulk_actions` e modifica dei Lead, accende le caselle e la
// barra sopra la tabella. «Pulizia dati (N)» (Task 18), per chi ha `merge_duplicates` e modifica dei Lead, come le rotte,
// apre la finestra dei doppioni e dei telefoni ed email da sistemare, anche dall'indirizzo (`pulizia=doppioni|telefoni`).
// «Importa da file» (M6 R6) apre la stessa finestra delle Impostazioni, soltanto con import_leads.

export type LeadListSearch = {
  q?: string;
  who?: "mine" | "all";
  sort?: LeadSortField;
  direction?: "asc" | "desc";
  /**
   * La definizione dei filtri (`FilterDefinition` v1); assente se non ci sono filtri. È un oggetto: il router lo scrive
   * nell'indirizzo come JSON una volta sola (`filter=%7B%22version%22…`). Un testo JSON, la forma dei link di prima,
   * scritta due volte (`filter=%22%7B%5C%22version…`), si legge ancora (decisione P25).
   */
  filter?: FilterDefinition;
  /** La finestra «Archiviati» aperta (piano M3 Task 12). */
  archiviati?: 1;
  /** La ricerca fra gli archiviati: un parametro suo, così non filtra la tabella dietro la finestra (decisione P16). */
  cercaArchiviati?: string;
  /** La finestra «Pulizia dati» aperta, con la scheda scelta (piano M3 Task 18). */
  pulizia?: CleanupTab;
};

function cleanupTabOf(value: unknown): CleanupTab | undefined {
  return value === "doppioni" || value === "telefoni" ? value : undefined;
}

function isSortField(value: unknown): value is LeadSortField {
  return typeof value === "string" && (LEAD_SORT_FIELDS as readonly string[]).includes(value);
}

/** I parametri dell'indirizzo `/lead`: un valore sconosciuto o di forma sbagliata vale come assente. */
export function validateLeadListSearch(search: Record<string, unknown>): LeadListSearch {
  const result: LeadListSearch = {};
  const q = searchParamText(search.q);
  if (q !== undefined) result.q = q;
  if (search.who === "mine" || search.who === "all") result.who = search.who;
  if (isSortField(search.sort)) result.sort = search.sort;
  if (search.direction === "asc" || search.direction === "desc") result.direction = search.direction;
  // Un filtro di forma sbagliata vale come nessun filtro; quello buono resta un oggetto, anche se arriva come testo.
  const filter = parseFilterParam(search.filter);
  if (!isEmptyFilter(filter)) result.filter = filter;
  // La finestra degli archiviati e la sua ricerca, che senza la finestra non vale.
  if (search.archiviati === 1 || search.archiviati === "1") {
    result.archiviati = 1;
    const archivedQ = searchParamText(search.cercaArchiviati);
    if (archivedQ !== undefined) result.cercaArchiviati = archivedQ;
  }
  const cleanupTab = cleanupTabOf(search.pulizia);
  if (cleanupTab !== undefined) result.pulizia = cleanupTab;
  return result;
}

/**
 * L'ordinamento dell'indirizzo; senza, quello della vista salvata in uso (LEAD-17), che di partenza è per data di creazione,
 * dalla più recente, come il server.
 */
function sortOf(search: LeadListSearch, saved: LeadSort): LeadSort {
  if (!search.sort) return search.direction ? { field: saved.field, direction: search.direction } : saved;
  return { field: search.sort, direction: search.direction ?? "asc" };
}

const SCOPE_RANK = { none: 0, own: 1, all: 2 } as const;

/** L'azione multipla di cui è aperta la finestra, con il gruppo fissato quando la si è aperta. */
type OpenBulk = { action: BulkAction; target: BulkTarget; picked: PickedLead[] };

export function LeadListPage() {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const search = useSearch({ from: "/autenticato/guscio/azienda/lead" });
  const navigate = useNavigate({ from: "/lead" });
  const searchId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const [creating, setCreating] = useState(false);
  const [arranging, setArranging] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const createListReasonId = useId();
  const organization = access.context.organization.name;
  const canCreate = leadScopeOf(access, "create") === "all";
  const canImport = (access.isPlatform || access.can("import_leads")) && view === null;
  const canCreateList = smartListScopeOf(access, "create") !== "none";
  // «Archiviati (N)»: con la funzione `trash_restore`, come la rotta del server. Il totale si legge con una riga sola.
  const canRestore = access.can("trash_restore");
  const archivedCount = useQuery({ ...archivedLeadsQuery(tab, { offset: 0, limit: 1 }, view), enabled: canRestore });
  // Di nuovo dalla forma, come il filtro qui sotto: un valore scartato da `validateLeadListSearch` resta grezzo.
  const archivedOpen = canRestore && search.archiviati === 1;
  const archivedQ = searchParamText(search.cercaArchiviati)?.trim() ?? "";
  // «Azioni multiple» (Task 13 e 14, D10): con la funzione `bulk_actions` e la modifica di qualche Lead, come la rotta.
  const toast = useToast();
  const updateScope = leadScopeOf(access, "update");
  const canBulk = access.can("bulk_actions") && updateScope !== "none";
  const [bulkOn, setBulkOn] = useState(false);
  const bulkActive = bulkOn && canBulk;
  const [bulkOpen, setBulkOpen] = useState<OpenBulk | null>(null);
  const bulkFeatures: Record<BulkFeature, boolean> = { change_owner: access.can("change_owner"), tags: access.can("manage_tags"), archive: access.can("trash_restore") };
  // Chi vede più Lead di quanti ne modifica: il server lascia fuori gli altri (Task 13), e finestra e avviso lo dicono.
  const leftOutPossible = SCOPE_RANK[updateScope] < SCOPE_RANK[leadScopeOf(access, "view")];
  // «Pulizia dati» (Task 18, D11 e D12): con la funzione `merge_duplicates` e la modifica di qualche Lead, come le rotte.
  const canClean = access.can("merge_duplicates") && updateScope !== "none";
  // Di nuovo dalla forma, come gli archiviati: un valore scartato da `validateLeadListSearch` resta grezzo.
  const cleanupTab = canClean ? cleanupTabOf(search.pulizia) : undefined;
  // Quando il pulsante appena premuto sparisce o si spegne («Seleziona tutti i N risultati», «Deseleziona», un'azione
  // confermata), il focus va sul riepilogo della barra, dopo il disegno che segue: mai sulla pagina (UX_SYSTEM).
  const bulkSummaryRef = useRef<HTMLParagraphElement>(null);
  const focusBulkSummary = useRef(false);
  useEffect(() => {
    if (!focusBulkSummary.current) return;
    focusBulkSummary.current = false;
    bulkSummaryRef.current?.focus();
  });

  // I filtri dell'indirizzo e i campi dell'azienda per il pannello «Filtri».
  // Di nuovo dalla forma: un valore che `validateLeadListSearch` scarta non sovrascrive quello grezzo dell'indirizzo, che
  // il router passa così com'è dalle rotte sopra (un testo qualunque, `?filter=%7Bnot-json`).
  const definition = useMemo<FilterDefinition>(() => parseFilterParam(search.filter), [search.filter]);
  const filtered = !isEmptyFilter(definition);
  const catalog = useQuery(leadCatalogQuery(tab));

  // Le colonne della vista in uso (D5): la disposizione del dispositivo, la vista personale, quella dell'azienda o
  // quella iniziale. I campi dell'azienda e le colonne di sistema visibili che costano una lettura si chiedono con l'elenco
  // (`fields=`, `include=`).
  const layout = useTableLayout({ tab, scope: LEADS_TABLE_SCOPE, t, catalog });
  // Le colonne cambiano subito nella tabella e restano sul dispositivo finché non si salvano (D5).
  const { tableColumns, widths, fields, include, moveColumn, resizeColumn, hideColumn } = useColumnArrange(layout);

  // La query di base, senza la pagina: tutte le pagine della stessa vista la condividono.
  const savedSort = savedSortOf(layout.server);
  const sort = sortOf(search, savedSort);
  const q = search.q?.trim() ?? "";
  const base = useMemo<Omit<LeadListQuery, "offset">>(
    () => ({
      ...(q ? { q } : {}),
      ...(search.who ? { who: search.who } : {}),
      ...(isEmptyFilter(definition) ? {} : { filter: definition }),
      ...(fields ? { fields } : {}),
      ...(include ? { include } : {}),
      sort: sort.field,
      direction: sort.direction,
      limit: LEAD_PAGE_SIZE,
    }),
    [q, search.who, definition, fields, include, sort.field, sort.direction],
  );
  // Una colonna in più o in meno rilegge le righe, ma la tabella non torna in cima.
  const scrollKey = JSON.stringify({ ...base, fields: undefined, include: undefined });

  // La prima pagina porta il totale e «Di chi»; le altre si leggono quando entrano nella finestra.
  const { first, total, rowAt, handleRange, failedPages, loadedPages, key: readKey } = useLeadPages({ tab, base, view });

  // Il numero di «Pulizia dati» somma le coppie e le righe da sistemare, lette con una riga sola ciascuna; senza nulla da
  // sistemare il pulsante non ha numero (mock-up). Le righe il server le calcola tutte anche per una sola (Task 17): le due
  // letture partono quando la prima pagina dell'elenco c'è già, così non le fanno concorrenza (la finestra le legge da sé), e
  // restano fresche un minuto, così tornare sull'elenco non le rifà ogni volta (decisione P56).
  const countsWanted = canClean && first.data !== undefined;
  const duplicatesCount = useQuery({ ...duplicatesQuery(tab, { offset: 0, limit: 1 }, view), enabled: countsWanted });
  const issuesCount = useQuery({ ...dataIssuesQuery(tab, { offset: 0, limit: 1 }, view), enabled: countsWanted });
  const cleanupCount = duplicatesCount.data && issuesCount.data ? duplicatesCount.data.total + issuesCount.data.total : 0;

  // Le caselle delle azioni multiple seguono l'elenco: ricerca, «Di chi» e filtri (non l'ordinamento né le colonne).
  const listKey = JSON.stringify({ q, who: search.who ?? null, filter: isEmptyFilter(definition) ? null : definition });
  const bulk = useBulkSelection({ list: listKey, read: readKey, pages: loadedPages });
  const { clear: clearBulk } = bulk;
  const tableSelection = useMemo(
    () => (bulkActive ? { picked: bulk.pickedIds, page: bulk.page, onPick: bulk.pick, onPickPage: bulk.pickPage } : undefined),
    [bulkActive, bulk.pickedIds, bulk.page, bulk.pick, bulk.pickPage],
  );

  // Il testo della ricerca: l'indirizzo si aggiorna 200 ms dopo l'ultimo tasto.
  const writeQ = useCallback((next: string | undefined) => void navigate({ search: (previous) => ({ ...previous, q: next }), replace: true }), [navigate]);
  const { text, setText } = useResultSearch(search.q, writeQ);

  function changeSort(field: LeadSortField, chosen?: LeadSort["direction"]) {
    const direction = chosen ?? (sort.field === field && sort.direction === "asc" ? "desc" : "asc");
    void navigate({ search: (previous) => ({ ...previous, sort: field, direction }), replace: true });
  }

  /** L'ordinamento torna a venire dalla vista salvata: esce dall'indirizzo (dopo un salvataggio, o tornando a una vista). */
  function adoptViewSort() {
    void navigate({ search: (previous) => ({ ...previous, sort: undefined, direction: undefined }), replace: true });
  }

  function changeWho(who: string) {
    if (who !== "mine" && who !== "all") return;
    void navigate({ search: (previous) => ({ ...previous, who }), replace: true });
  }

  function resetSearch() {
    setText("");
    void navigate({ search: (previous) => ({ ...previous, q: undefined }), replace: true });
    searchRef.current?.focus();
  }

  // I filtri si applicano subito: ogni scelta nel pannello riscrive l'indirizzo, senza una voce in più nella cronologia.
  function changeFilter(next: FilterDefinition) {
    void navigate({ search: (previous) => ({ ...previous, filter: isEmptyFilter(next) ? undefined : next }), replace: true });
  }

  function resetFilters() {
    void navigate({ search: (previous) => ({ ...previous, filter: undefined }), replace: true });
  }

  // La finestra degli archiviati sta nell'indirizzo, come la sua ricerca; chiusa, li toglie tutti e due.
  function openArchived() {
    void navigate({ search: (previous) => ({ ...previous, archiviati: 1 }), replace: true });
  }

  function closeArchived() {
    void navigate({ search: (previous) => ({ ...previous, archiviati: undefined, cercaArchiviati: undefined }), replace: true });
  }

  // La finestra «Pulizia dati» sta nell'indirizzo con la sua scheda: «Indietro» dalla scheda di un Lead la ritrova.
  function openCleanup() {
    void navigate({ search: (previous) => ({ ...previous, pulizia: "doppioni" }), replace: true });
  }

  function chooseCleanupTab(next: CleanupTab) {
    void navigate({ search: (previous) => ({ ...previous, pulizia: next }), replace: true });
  }

  function closeCleanup() {
    void navigate({ search: (previous) => ({ ...previous, pulizia: undefined }), replace: true });
  }

  const writeArchivedQ = useCallback(
    (next: string | undefined) => void navigate({ search: (previous) => ({ ...previous, cercaArchiviati: next }), replace: true }),
    [navigate],
  );

  function toggleBulk() {
    const next = !bulkActive;
    setBulkOn(next);
    clearBulk();
    toast.show(next ? t("bulkActions.on") : t("bulkActions.off"));
  }

  // La finestra fissa il gruppo: gli id spuntati, oppure la vista di adesso con il numero che si vede (decisione P1).
  function openBulk(action: BulkAction) {
    if (bulk.selection.mode === "all") {
      setBulkOpen({ action, target: queryTargetOf({ q, who: search.who, filter: definition }, total), picked: [] });
      return;
    }
    const picked = [...bulk.selection.picked.values()];
    if (picked.length === 0 || bulk.pending) return;
    setBulkOpen({ action, target: { kind: "ids", ids: picked.map((lead) => lead.id) }, picked });
  }

  function bulkDone(body: BulkActionBody, response: BulkActionResponse) {
    setBulkOpen(null);
    clearBulk();
    focusBulkSummary.current = true;
    toast.show(bulkOutcomeText(t, locale, { body, response, leftOutPossible }), "success");
  }

  function selectAllResults() {
    bulk.selectAll();
    focusBulkSummary.current = true;
  }

  function clearSelection() {
    clearBulk();
    focusBulkSummary.current = true;
  }

  function resetSearchAndFilters() {
    setText("");
    void navigate({ search: (previous) => ({ ...previous, q: undefined, filter: undefined }), replace: true });
    searchRef.current?.focus();
  }

  const data = first.data;
  const who = data?.who;
  // «I miei» scelto sull'elenco (non il perimetro di chi vede soltanto i suoi): una lista non lo salva.
  const mineActive = data?.who === "mine" && data.canChooseWho;
  // Di chi sono i Lead e se una ricerca o dei filtri li restringono: mai «Tutti» per un elenco ristretto (decisione P25).
  const scope = captionScope(t, { mine: who === "mine", searching: q !== "", filtered, organization });
  const words = sortDescription(t, layout.catalog, sort);
  const caption = t("leadList.captionSorted", { scope, column: words.column, direction: words.direction });
  const count = data ? (total === 1 ? t("leadList.countOne") : t("leadList.countMany", { count: formatCount(locale, total) })) : t("common.loading");

  let content: ReactNode;
  if (first.isError && !data) {
    // Un filtro che il server rifiuta (un campo tolto nel frattempo) non passa riprovando: si tolgono i filtri.
    const filterRefused = first.error instanceof ApiRequestError && first.error.code === "filter_invalid";
    content = (
      <div className="g-lead-list__problem">
        <Reason tone="warning" role="alert">{messageForError(t, first.error)}</Reason>
        {filterRefused ? (
          <Button onClick={resetFilters}>{t("leadFilters.reset")}</Button>
        ) : (
          <Button onClick={() => void first.refetch()}>{t("common.retry")}</Button>
        )}
      </div>
    );
  } else if (data && total === 0 && !first.isPlaceholderData) {
    content = (
      <EmptyState q={q} filtered={filtered} data={data} organization={organization} onReset={resetSearch} onResetAll={resetSearchAndFilters} />
    );
  } else {
    content = (
      <>
        {failedPages.length > 0 ? (
          <div className="g-lead-list__problem">
            <Reason tone="warning" role="alert">{t("leadList.pagesFailed")}</Reason>
            <Button onClick={() => failedPages.forEach((result) => void result.refetch())}>{t("common.retry")}</Button>
          </div>
        ) : null}
        <LeadTable
          caption={caption}
          total={data ? total : LOADING_ROWS}
          totalKnown={data !== undefined}
          rowAt={rowAt}
          sort={sort}
          onSort={changeSort}
          onRangeChange={handleRange}
          resetKey={scrollKey}
          busy={first.isPlaceholderData || first.isPending}
          timeZone={access.context.settings.timeZone}
          columns={tableColumns}
          widths={widths}
          onMove={moveColumn}
          onResize={resizeColumn}
          onHide={hideColumn}
          selection={tableSelection}
        />
      </>
    );
  }

  return (
    <div className="g-lead-list">
      <PageHeader
        eyebrow={organization}
        title={t("shell.nav.leads")}
        actions={
          canCreate || canCreateList || canBulk || canClean || canImport ? (
            <>
              {canClean ? (
                <Button aria-haspopup="dialog" onClick={openCleanup}>
                  <Icon name="wand" />
                  {cleanupCount > 0 ? t("dataCleanup.buttonCount", { count: formatCount(locale, cleanupCount) }) : t("dataCleanup.button")}
                </Button>
              ) : null}
              {canBulk ? (
                <Button aria-pressed={bulkActive} onClick={toggleBulk}>
                  <Icon name="check" />
                  {t("bulkActions.button")}
                </Button>
              ) : null}
              {canCreateList ? (
                <Button
                  aria-haspopup="dialog"
                  disabled={!filtered}
                  aria-describedby={filtered ? undefined : createListReasonId}
                  onClick={() => setCreatingList(true)}
                >
                  {t("smartLists.create")}
                </Button>
              ) : null}
              {canImport ? <FileImportButton /> : null}
              {canCreate ? (
                <Button variant="primary" aria-haspopup="dialog" onClick={() => setCreating(true)}>
                  <Icon name="plus" />
                  {t("leadList.newLead")}
                </Button>
              ) : null}
              {canCreateList && !filtered ? (
                <Reason id={createListReasonId} className="g-lead-list__actions-reason">
                  {t("smartLists.createReason")}
                </Reason>
              ) : null}
            </>
          ) : null
        }
      />
      <p className="g-lead-list__intro">{t("leadList.intro")}</p>
      <div className="g-lead-list__toolbar">
        <div className="g-lead-search">
          <Icon name="search" className="g-lead-search__icon" />
          <label className="g-visually-hidden" htmlFor={searchId}>{t("leadList.searchLabel")}</label>
          <input
            ref={searchRef}
            id={searchId}
            className="g-field__input"
            type="search"
            value={text}
            maxLength={SEARCH_MAX_LENGTH}
            autoComplete="off"
            placeholder={t("leadList.searchPlaceholder")}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <Button aria-haspopup="dialog" onClick={() => setArranging(true)}>
          <Icon name="columns" />
          {t("tableView.button")}
        </Button>
        {data?.canChooseWho ? (
          <Segmented
            label={t("leadList.whoLabel")}
            options={[
              { value: "mine", label: t("leadList.whoMine") },
              { value: "all", label: t("leadList.whoAll") },
            ]}
            // La scelta appena fatta si vede subito, anche prima che arrivino le righe.
            value={search.who ?? data.who}
            onChange={changeWho}
          />
        ) : null}
        <span className="g-lead-list__spacer" />
        <strong className="g-lead-list__count" role="status" aria-live="polite">
          {count}
        </strong>
      </div>
      <FilterBar
        definition={definition}
        onChange={changeFilter}
        onReset={resetFilters}
        catalog={catalog.data}
        tab={tab}
        resultText={count}
        narrowed={q !== "" || (data?.who === "mine" && data.canChooseWho)}
      />
      {bulkActive ? (
        <BulkBar
          all={bulk.selection.mode === "all"}
          count={bulk.selection.mode === "all" ? total : bulk.selection.picked.size}
          total={data && !first.isPlaceholderData ? total : null}
          features={bulkFeatures}
          onSelectAll={selectAllResults}
          onClear={clearSelection}
          onAction={openBulk}
          summaryRef={bulkSummaryRef}
          pending={bulk.pending}
        />
      ) : null}
      {content}
      {canRestore ? (
        <div className="g-archived-link">
          <Button variant="ghost" aria-haspopup="dialog" onClick={openArchived}>
            <Icon name="trash" />
            {archivedCount.data ? t("leadArchive.link", { count: formatCount(locale, archivedCount.data.total) }) : t("leadArchive.title")}
          </Button>
        </div>
      ) : null}
      {bulkOpen && bulkActive ? (
        <BulkDialog
          tab={tab}
          action={bulkOpen.action}
          target={bulkOpen.target}
          picked={bulkOpen.picked}
          leftOutPossible={leftOutPossible}
          canCreateTags={bulkFeatures.tags}
          onDone={bulkDone}
          onClose={() => setBulkOpen(null)}
        />
      ) : null}
      {creating ? (
        <NewLeadDialog tab={tab} canChangeOwner={access.can("change_owner")} canOpenArchived={canRestore} onClose={() => setCreating(false)} />
      ) : null}
      {cleanupTab ? (
        <DataCleanupDialog
          tab={tab}
          current={cleanupTab}
          onTab={chooseCleanupTab}
          timeZone={access.context.settings.timeZone}
          canChangeOwner={access.can("change_owner")}
          onClose={closeCleanup}
        />
      ) : null}
      {archivedOpen ? (
        <ArchivedDialog tab={tab} q={archivedQ} onSearch={writeArchivedQ} timeZone={access.context.settings.timeZone} onClose={closeArchived} />
      ) : null}
      {creatingList && filtered ? (
        <NewSmartListDialog
          tab={tab}
          definition={definition}
          sort={sort}
          forOthers={smartListScopeOf(access, "update") === "all"}
          mineActive={mineActive}
          onCreated={(smartListId) => {
            setCreatingList(false);
            void navigate({ to: "/smart-list/$smartListId", params: { smartListId } });
          }}
          onClose={() => setCreatingList(false)}
        />
      ) : null}
      {arranging ? (
        <ColumnsDrawer
          tab={tab}
          scope={LEADS_TABLE_SCOPE}
          organization={organization}
          context={t("tableView.contextLeads")}
          layout={layout}
          sort={{ current: sort, saved: savedSort, onAdopted: adoptViewSort }}
          onClose={() => setArranging(false)}
        />
      ) : null}
    </div>
  );
}

/** La prima parte della didascalia della tabella: i Lead di chi e, se ristretti, trovati con la ricerca o filtrati. */
function captionScope(t: Translate, input: { mine: boolean; searching: boolean; filtered: boolean; organization: string }): string {
  const { mine, searching, filtered, organization } = input;
  if (searching) return mine ? t("leadList.captionMineFound") : t("leadList.captionFound", { organization });
  if (filtered) return mine ? t("leadList.captionMineFiltered") : t("leadList.captionFiltered", { organization });
  return mine ? t("leadList.captionMine") : t("leadList.captionAll", { organization });
}

type EmptyProps = {
  q: string;
  filtered: boolean;
  data: LeadListResponse;
  organization: string;
  onReset: () => void;
  onResetAll: () => void;
};

/**
 * Tre vuoti diversi (UX_SYSTEM, matrice degli stati): nessun Lead per questi filtri (con la ricerca, se c'è), con
 * «Azzera ricerca e filtri»; nessun Lead per questa ricerca, con «Azzera la ricerca»; oppure nessun Lead del tutto
 * («Nuovo Lead» resta in alto, per chi può crearli).
 */
function EmptyState({ q, filtered, data, organization, onReset, onResetAll }: EmptyProps) {
  const t = useT();
  if (filtered) {
    return (
      <div className="g-lead-list__empty">
        <p className="g-lead-list__empty-title">{t("leadFilters.emptyTitle")}</p>
        <p>{data.who === "mine" && data.canChooseWho ? t("leadFilters.emptyHintMine") : t("leadFilters.emptyHint")}</p>
        <Button onClick={onResetAll}>{t("leadFilters.resetAll")}</Button>
      </div>
    );
  }
  if (q) {
    return (
      <div className="g-lead-list__empty">
        <p className="g-lead-list__empty-title">{t("leadList.noResultsTitle", { q })}</p>
        <p>{t("leadList.noResultsText")}</p>
        {data.who === "mine" && data.canChooseWho ? <p>{t("leadList.noResultsMine")}</p> : null}
        <Button onClick={onReset}>{t("leadList.resetSearch")}</Button>
      </div>
    );
  }
  if (data.who === "mine") {
    return (
      <div className="g-lead-list__empty">
        <p className="g-lead-list__empty-title">{t("leadList.emptyMineTitle")}</p>
        <p>{data.canChooseWho ? t("leadList.emptyMineChoose") : t("leadList.emptyMineWait")}</p>
      </div>
    );
  }
  return (
    <div className="g-lead-list__empty">
      <p className="g-lead-list__empty-title">{t("leadList.emptyTitle")}</p>
      <p>{t("leadList.emptyAll", { organization })}</p>
    </div>
  );
}
